import React, { useState, useEffect, useRef } from 'react';
import { generateVideoPrompt, transcribeDialogueAudio } from '../services/geminiService';
import { generateLipsyncWithPixverse } from '../services/falService';
import { generateSpeechWithElevenLabs } from '../services/elevenlabsService';
import { getImageToVideoModels, getStartEndFrameVideoModels, getAllVideoModels, generateVideoProductionPrompt } from '../services/aiService';
import { uploadDataUriToBackend } from '../services/backendService';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { CloseIcon } from './icons/CloseIcon';
import { MagicWandIcon } from './icons/MagicWandIcon';
import { DownloadIcon } from './icons/DownloadIcon';
import { SaveIcon } from './icons/SaveIcon';
import { MicrophoneIcon } from './icons/MicrophoneIcon';
import { StopCircleIcon } from './icons/StopCircleIcon';
import { TrashIcon } from './icons/TrashIcon';
import { PlusIcon } from './icons/PlusIcon';
import { v4 as uuidv4 } from 'uuid';
import { ElevenLabsVoiceModal } from './ElevenLabsVoiceModal';
import { CREDIT_COSTS } from '../types';

// Fallback models if backend returns empty (based on common video generation models)
const DEFAULT_VIDEO_MODELS = [
    { id: 'kling-2.5-turbo', name: 'Kling 2.5 Turbo', provider: 'Kling', disabled: false },
    { id: 'wan-2.2', name: 'Wan 2.2', provider: 'Wan', disabled: false },
    { id: 'gemini-3-fast-preview', name: 'Gemini 3 Fast Preview', provider: 'Google', disabled: false },
];

const CAMERA_SHOTS = [
    'Static Shot', 'Cinematic Pan Left', 'Cinematic Pan Right', 'Tilt Up', 'Tilt Down',
    'Zoom In', 'Zoom Out', 'Dolly Zoom', 'Crane Shot', 'Aerial Shot', 'Handheld Shot'
];

const FONT_OPTIONS = [
    'Cinematic', 'Serif', 'Sans-serif', 'Handwriting', 'Futuristic', 'Horror', 'Elegant'
];

const Switch = ({ checked, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`${checked ? 'bg-purple-600' : 'bg-gray-600'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
    >
        <span className={`${checked ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
    </button>
);

const TabButton = ({ name, tabId, activeTab, setActiveTab }) => (
    <button
        onClick={() => setActiveTab(tabId)}
        className={`flex-1 py-3 text-center text-sm font-semibold transition-colors ${activeTab === tabId ? 'bg-purple-600/20 text-purple-300 border-b-2 border-purple-400' : 'text-gray-400 hover:bg-gray-700/50'}`}
    >
        {name}
    </button>
);

const DialogueAudioSection = ({ character, dialogue, onDialogueChange, onGenerateSpeech, generationStatus, generationError, onSelectVoice }) => {
    const [recordingStatus, setRecordingStatus] = useState('idle');
    const [sourceAudio, setSourceAudio] = useState(null);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [transcriptionError, setTranscriptionError] = useState(null);
    const [recordError, setRecordError] = useState(null);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const handleStartRecording = async () => {
        setRecordError(null);
        setSourceAudio(null);
        setTranscriptionError(null);
        onDialogueChange({ clonedAudio: undefined, text: '' });
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];
            
            mediaRecorderRef.current.ondataavailable = event => audioChunksRef.current.push(event.data);
            
            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                stream.getTracks().forEach(track => track.stop());

                if (audioBlob.size === 0) {
                    console.warn("Recorded audio blob is empty.");
                    setRecordingStatus('idle');
                    return;
                }

                const url = URL.createObjectURL(audioBlob);
                setRecordingStatus('recorded');
                
                const reader = new FileReader();
                reader.onloadend = async () => {
                    const base64Data = reader.result;
                    setSourceAudio({
                        url,
                        data: {
                            name: `dialogue-rec-${character.id}-${Date.now()}.webm`,
                            data: base64Data,
                            mimeType: 'audio/webm',
                        }
                    });

                    setIsTranscribing(true);
                    setTranscriptionError(null);
                    try {
                        const transcript = await transcribeDialogueAudio(base64Data);
                        onDialogueChange({ text: transcript });
                    } catch (err) {
                        const msg = err instanceof Error ? err.message : "Transcription failed.";
                        setTranscriptionError(msg);
                    } finally {
                        setIsTranscribing(false);
                    }
                };
                reader.readAsDataURL(audioBlob);
            };
            
            mediaRecorderRef.current.start();
            setRecordingStatus('recording');
        } catch (err) {
            console.error(err);
            setRecordError("Microphone access denied.");
        }
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
    };
    
    const handleResetRecording = () => {
        if (sourceAudio?.url) {
            URL.revokeObjectURL(sourceAudio.url);
        }
        setSourceAudio(null);
        onDialogueChange({ clonedAudio: undefined });
        setRecordingStatus('idle');
        setRecordError(null);
    };

    useEffect(() => {
        const url = sourceAudio?.url;
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [sourceAudio]);
    
    const hasClonedVoice = !!character.elevenLabsVoiceId;
    const isProcessing = generationStatus === 'generating' || isTranscribing;

    const displayVoiceName = character.audio?.name || (character.elevenLabsVoiceId ? `ID: ${character.elevenLabsVoiceId.substring(0, 8)}...` : '');

    return (
        <div className="space-y-2 pb-2 border-b border-gray-800 last:border-0">
            <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center gap-1">
                    <input
                        type="text"
                        value={dialogue.startTimecode}
                        onChange={(e) => onDialogueChange({ startTimecode: e.target.value })}
                        placeholder="0.0s"
                        className="w-14 text-xs p-1 bg-gray-700/60 border border-gray-600 rounded-md"
                        disabled={isProcessing}
                        aria-label={`Start timecode for ${character.name}`}
                    />
                    <span className="text-gray-500">-</span>
                    <input
                        type="text"
                        value={dialogue.endTimecode}
                        onChange={(e) => onDialogueChange({ endTimecode: e.target.value })}
                        placeholder="3.0s"
                        className="w-14 text-xs p-1 bg-gray-700/60 border border-gray-600 rounded-md"
                        disabled={isProcessing}
                        aria-label={`End timecode for ${character.name}`}
                    />
                </div>
                <label className="text-xs font-bold text-gray-300 flex-grow truncate">{character.name}</label>
                {recordingStatus === 'idle' && (
                    <button onClick={handleStartRecording} disabled={isProcessing} className="p-1 rounded-full hover:bg-gray-600 disabled:opacity-50">
                        <MicrophoneIcon className="w-4 h-4 text-gray-400 hover:text-white" />
                    </button>
                )}
                 {recordingStatus === 'recording' && (
                    <button onClick={handleStopRecording} className="p-1 rounded-full text-red-400 bg-red-500/20 animate-pulse">
                         <StopCircleIcon className="w-4 h-4" />
                    </button>
                )}
            </div>
            
            <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
                <span className="truncate max-w-[200px]">
                    {hasClonedVoice ? (
                        <>Voice: <span className="text-green-400 font-mono">{displayVoiceName}</span></>
                    ) : (
                        <span className="text-yellow-500">No voice assigned</span>
                    )}
                </span>
                {onSelectVoice && (
                    <button 
                        onClick={onSelectVoice} 
                        className="text-xs text-blue-400 hover:text-blue-300 underline"
                    >
                        {hasClonedVoice ? 'Change Voice' : 'Select Voice'}
                    </button>
                )}
            </div>

            {recordError && <p className="text-xs text-red-400">{recordError}</p>}
            <textarea
                value={dialogue.text}
                onChange={(e) => onDialogueChange({ text: e.target.value })}
                rows={2}
                placeholder={isTranscribing ? "Transcribing your dialogue..." : `Enter dialogue for ${character.name}...`}
                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md resize-none text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={isProcessing}
            />
            {transcriptionError && <p className="text-xs text-red-400 mt-1">{transcriptionError}</p>}
            {sourceAudio && (
                 <div className="bg-gray-900/50 p-2 rounded-md space-y-2">
                     <div className="flex items-center gap-2">
                        <audio src={sourceAudio.url} controls className="w-full h-8" />
                        <button
                            onClick={handleResetRecording}
                            className="p-1.5 rounded-full hover:bg-red-500/20 text-red-400 flex-shrink-0"
                            aria-label="Delete recording and record again"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                 </div>
            )}
            <button
                onClick={onGenerateSpeech}
                disabled={!hasClonedVoice || !dialogue.text || isProcessing}
                className="w-full text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-1.5 px-3 rounded-md flex items-center justify-center"
            >
                {generationStatus === 'generating' ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <MagicWandIcon className="w-4 h-4 mr-2" />}
                {generationStatus === 'generating' ? 'Generating...' : 'Generate Audio'}
            </button>
            {!hasClonedVoice && (
                <p className="text-xs text-yellow-400 mt-1">Please select a voice for this character to enable AI audio generation.</p>
            )}
            {generationStatus === 'error' && generationError && <p className="text-xs text-red-400">{generationError}</p>}
            {dialogue.clonedAudio && (
                 <div className="bg-green-900/30 p-2 rounded-md space-y-2 mt-2">
                    <p className="text-xs text-green-300 font-semibold">Generated Audio:</p>
                    <audio src={dialogue.clonedAudio.data} controls className="w-full h-8" />
                 </div>
            )}
        </div>
    );
};

export const VideoPreProductionModal = ({ shot, characters, onClose, onSave, projectData, onDeductCredits, onTrackCreditsUsed, onUpdateCharacter }) => {
    const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(true);
    const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
    const [activeTab, setActiveTab] = useState('prompt');
    
    const [showVoiceModal, setShowVoiceModal] = useState(false);
    const [selectingVoiceForCharId, setSelectingVoiceForCharId] = useState(null);
    
    const [timedActions, setTimedActions] = useState([]);
    
    const [dialogues, setDialogues] = useState(() => {
        const initial = {};
        const allRelevantChars = [...characters, ...projectData.characters.filter(c => c.category === 'narrator')];
        
        // First, process dialogues in the order they appear in shot.dialogues (which should be script order)
        // This ensures PRIYA speaks before ANIKA if that's the script order
        let currentTime = 0;
        const processedCharIds = new Set();
        
        // Sort shot.dialogues by their order/index/startTime if available
        const sortedDialogues = [...(shot.dialogues || [])].sort((a, b) => {
            // Try different ordering fields
            if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
            if (a.index !== undefined && b.index !== undefined) return a.index - b.index;
            if (a.startTime !== undefined && b.startTime !== undefined) {
                const aTime = typeof a.startTime === 'string' ? parseFloat(a.startTime) : a.startTime;
                const bTime = typeof b.startTime === 'string' ? parseFloat(b.startTime) : b.startTime;
                return aTime - bTime;
            }
            if (a.start_time !== undefined && b.start_time !== undefined) {
                const aTime = typeof a.start_time === 'string' ? parseFloat(a.start_time) : a.start_time;
                const bTime = typeof b.start_time === 'string' ? parseFloat(b.start_time) : b.start_time;
                return aTime - bTime;
            }
            // Keep original order if no ordering field
            return 0;
        });
        
        console.log('🎭 Processing dialogues in script order:', sortedDialogues.map(d => ({
            characterId: d.characterId,
            characterName: d.characterName,
            order: d.order,
            startTime: d.startTime || d.start_time,
            text: d.text?.substring(0, 30) + '...'
        })));
        
        // Process dialogues in script order first
        sortedDialogues.forEach(dialogue => {
            const charId = dialogue.characterId;
            if (!charId || processedCharIds.has(charId)) return;
            
            const char = allRelevantChars.find(c => c.id === charId);
            if (!char) return;
            
            const text = dialogue.text || '';
            const wordCount = text.trim().split(/\s+/).length;
            const estimatedDuration = text ? Math.max(2, Math.ceil(wordCount / 2.5)) : 3;
            
            // Use provided timecodes if available, otherwise calculate
            let start = currentTime;
            let end = currentTime + estimatedDuration;
            
            if (dialogue.startTime !== undefined || dialogue.start_time !== undefined) {
                const providedStart = dialogue.startTime || dialogue.start_time;
                start = typeof providedStart === 'string' ? parseFloat(providedStart) : providedStart;
            }
            if (dialogue.endTime !== undefined || dialogue.end_time !== undefined) {
                const providedEnd = dialogue.endTime || dialogue.end_time;
                end = typeof providedEnd === 'string' ? parseFloat(providedEnd) : providedEnd;
            } else if (dialogue.startTime !== undefined || dialogue.start_time !== undefined) {
                // If only start time provided, calculate end based on duration
                end = start + estimatedDuration;
            }
            
            initial[charId] = { 
                startTimecode: `${start.toFixed(1)}s`, 
                endTimecode: `${end.toFixed(1)}s`, 
                text: text,
                order: dialogue.order || sortedDialogues.indexOf(dialogue)
            };
            
            processedCharIds.add(charId);
            currentTime = Math.max(currentTime, end);
        });
        
        // Then add any remaining characters that weren't in shot.dialogues
        allRelevantChars.forEach(c => {
            if (processedCharIds.has(c.id)) return;
            
            const isNarrator = c.category === 'narrator';
            let text = '';
            if (isNarrator) {
                text = shot.narration || '';
            }

            const wordCount = text.trim().split(/\s+/).length;
            const estimatedDuration = text ? Math.max(2, Math.ceil(wordCount / 2.5)) : 3;
            
            const start = text ? currentTime : 0;
            const end = text ? currentTime + estimatedDuration : 3;
            
            if (text) {
                currentTime += estimatedDuration;
            }

            initial[c.id] = { 
                startTimecode: `${start.toFixed(1)}s`, 
                endTimecode: `${end.toFixed(1)}s`, 
                text: text || '',
                order: 999 // Put unordered characters at the end
            };
        });
        
        return initial;
    });

    const [speechGenStatus, setSpeechGenStatus] = useState({});

    const [textOverlays, setTextOverlays] = useState(shot.textOverlays || []);
    const narrator = projectData.characters.find(c => c.category === 'narrator');
    
    // Dynamic video models from backend
    const [availableVideoModels, setAvailableVideoModels] = useState(DEFAULT_VIDEO_MODELS);
    const [isLoadingModels, setIsLoadingModels] = useState(true);
    
    // Determine video model type based on available frames
    const hasBothFrames = !!(shot.startFrameImage && shot.endFrameImage);
    const hasOnlyOneFrame = !!(shot.startFrameImage || shot.endFrameImage) && !hasBothFrames;
    
    // Fetch available video models from backend based on frame availability
    useEffect(() => {
        const fetchModels = async () => {
            setIsLoadingModels(true);
            try {
                let models = [];
                
                // ALWAYS use localStorage as the source of truth for model selections
                // The AIToolkitSelector saves selections to localStorage immediately when user clicks a model
                // projectData.modelPreferences may be stale/outdated
                let selectedModels = {};
                
                try {
                    const stored = localStorage.getItem('selectedAIModels');
                    if (stored) {
                        selectedModels = JSON.parse(stored);
                        console.log('🎬 Using selectedModels from localStorage (source of truth)');
                    }
                } catch (e) {
                    console.warn('🎬 Failed to parse selectedAIModels from localStorage:', e);
                }
                
                // Fallback to projectData only if localStorage is empty
                if (Object.keys(selectedModels).length === 0) {
                    if (projectData.modelPreferences?.selectedModels && Object.keys(projectData.modelPreferences.selectedModels).length > 0) {
                        selectedModels = projectData.modelPreferences.selectedModels;
                        console.log('🎬 Fallback: Using selectedModels from projectData.modelPreferences');
                    } else if (projectData.selectedModels && Object.keys(projectData.selectedModels).length > 0) {
                        selectedModels = projectData.selectedModels;
                        console.log('🎬 Fallback: Using selectedModels from projectData.selectedModels');
                    }
                }
                
                console.log('🎬 All selected models from AI Toolkit:', selectedModels);
                console.log('🎬 Available keys:', Object.keys(selectedModels));
                
                let defaultModelKey;
                if (hasBothFrames) {
                    // For start-end-frame, look for the 'start-end-frame videos' capability
                    defaultModelKey = selectedModels['start-end-frame videos'] || 
                                      selectedModels['start-end-frame video'] ||
                                      selectedModels['startEndFrame'] ||
                                      selectedModels['start_end_frame_videos'];
                    console.log('🎬 Looking for start-end-frame default model:', defaultModelKey);
                } else {
                    // For image-to-video, look for the 'Image to video' capability
                    defaultModelKey = selectedModels['Image to video'] || 
                                      selectedModels['image-to-video'] ||
                                      selectedModels['imageToVideo'] ||
                                      selectedModels['image_to_video'];
                    console.log('🎬 Looking for image-to-video default model:', defaultModelKey);
                }
                
                if (hasBothFrames) {
                    // Both frames available - show ONLY start-end-frame models
                    console.log('🎬 Shot has BOTH frames - showing ONLY start-end-frame video models');
                    const allModels = await getAllVideoModels();
                    models = allModels.startEndFrame || [];
                } else if (hasOnlyOneFrame) {
                    // Only one frame - show ONLY image-to-video models
                    console.log('🎬 Shot has ONE frame - showing ONLY image-to-video models');
                    const allModels = await getAllVideoModels();
                    models = allModels.imageToVideo || [];
                } else {
                    // No frames - show text-to-video or all available
                    console.log('🎬 Shot has NO frames - showing all video models');
                    const allModels = await getAllVideoModels();
                    models = [...(allModels.imageToVideo || []), ...(allModels.startEndFrame || [])];
                }
                
                console.log('🎬 Fetched video models:', models.map(m => m.name || m.key));
                console.log('🎬 Default model key from AI Toolkit:', defaultModelKey);
                
                if (models && models.length > 0) {
                    // Transform backend model format to component format
                    let transformedModels = models.map(m => ({
                        id: m.key || m.id || m.name,
                        name: m.name || m.key || m.id,
                        provider: m.provider || '',
                        description: m.description || '',
                        costEstimate: m.cost_estimate || '',
                        disabled: m.disabled || false
                    }));
                    
                    // Remove duplicates while preserving order
                    transformedModels = transformedModels.filter((m, idx, arr) => 
                        arr.findIndex(x => x.id === m.id) === idx
                    );
                    
                    console.log('🎬 Transformed models before reorder:', transformedModels.map(m => ({ id: m.id, name: m.name })));
                    
                    // Put the default model from AI Toolkit at the TOP of the list
                    if (defaultModelKey) {
                        const defaultKeyLower = defaultModelKey.toLowerCase().trim();
                        console.log('🎬 Searching for default model with key:', defaultKeyLower);
                        
                        const defaultModelIndex = transformedModels.findIndex(m => {
                            const modelId = (m.id || '').toLowerCase().trim();
                            const modelName = (m.name || '').toLowerCase().trim();
                            
                            // Exact match first
                            const exactMatch = modelId === defaultKeyLower || modelName === defaultKeyLower;
                            
                            // Then partial/contains match
                            const partialMatch = modelId.includes(defaultKeyLower) || 
                                                 defaultKeyLower.includes(modelId) ||
                                                 modelName.includes(defaultKeyLower) ||
                                                 defaultKeyLower.includes(modelName);
                            
                            if (exactMatch || partialMatch) {
                                console.log(`🎬 Found match: model=${m.name}, id=${m.id}, exactMatch=${exactMatch}, partialMatch=${partialMatch}`);
                            }
                            
                            return exactMatch || partialMatch;
                        });
                        
                        console.log('🎬 Default model index in list:', defaultModelIndex);
                        
                        if (defaultModelIndex > 0) {
                            // Move default model to top
                            const [defaultModel] = transformedModels.splice(defaultModelIndex, 1);
                            transformedModels.unshift(defaultModel);
                            console.log('🎬 Moved default model to top:', defaultModel.name);
                        } else if (defaultModelIndex === 0) {
                            console.log('🎬 Default model already at top:', transformedModels[0].name);
                        } else {
                            console.warn('🎬 Default model not found in available models. Looking for:', defaultKeyLower);
                            console.warn('🎬 Available model IDs:', transformedModels.map(m => m.id));
                            console.warn('🎬 Available model names:', transformedModels.map(m => m.name));
                        }
                    } else {
                        console.warn('🎬 No default model key found in AI Toolkit selections');
                    }
                    
                    console.log('🎬 Final video models (default first):', transformedModels.map(m => m.name));
                    setAvailableVideoModels(transformedModels);
                } else {
                    console.warn('No video models from backend, using defaults');
                    setAvailableVideoModels(DEFAULT_VIDEO_MODELS);
                }
            } catch (err) {
                console.warn('Failed to fetch video models, using defaults:', err);
                setAvailableVideoModels(DEFAULT_VIDEO_MODELS);
            } finally {
                setIsLoadingModels(false);
            }
        };
        fetchModels();
    }, [hasBothFrames, hasOnlyOneFrame, projectData.modelPreferences, projectData.selectedModels]);
    
    const getInitialVideoModel = () => {
        // The default model from AI Toolkit is already at position 0 (moved to top during fetch)
        // So we just select the first non-disabled model
        const firstAvailableModel = availableVideoModels.find(m => !m.disabled);
        if (firstAvailableModel) {
            console.log('🎬 Using default model:', firstAvailableModel.name);
            return firstAvailableModel.id;
        }
        return availableVideoModels[0]?.id || 'wan-2.2';
    };

    const [videoModel, setVideoModel] = useState(getInitialVideoModel());
    const [cameraShot, setCameraShot] = useState(CAMERA_SHOTS[0]);
    const [sfx, setSfx] = useState('');
    const [vfx, setVfx] = useState('');
    const [bgm, setBgm] = useState('');
    const [ambient, setAmbient] = useState('');
    const [resolution, setResolution] = useState('auto');
    const [duration, setDuration] = useState(6);
    
    // Update video model when available models are loaded
    // Always select the first model (which is the default from AI Toolkit, moved to top)
    useEffect(() => {
        if (!isLoadingModels && availableVideoModels.length > 0) {
            // The first model in the list is the default from AI Toolkit
            const defaultModel = availableVideoModels.find(m => !m.disabled) || availableVideoModels[0];
            if (defaultModel && defaultModel.id !== videoModel) {
                console.log('🎬 Setting video model to default:', defaultModel.name);
                setVideoModel(defaultModel.id);
            }
        }
    }, [availableVideoModels, isLoadingModels]);

    useEffect(() => {
        if (videoModel === 'wan-2.2') {
            setResolution('480p');
            setDuration(5);
        } else if (videoModel === 'sora-2') {
            setResolution('auto');
            setDuration(4);
        } else if (videoModel === 'kling-2.6' || videoModel === 'kling-2.5-turbo') {
            setDuration(5);
        } else if (videoModel === 'ltx-fast') {
            setResolution('720p');
            setDuration(5);
        } else if (videoModel === 'minimax') {
            setDuration(5);
        }
    }, [videoModel]);

    // State for detailed production prompt from backend
    const [productionPrompt, setProductionPrompt] = useState(null);

    useEffect(() => {
        const fetchPrompt = async () => {
            setError(null);
            setIsGeneratingPrompt(true);
            try {
                // Try to get detailed production prompt from backend first
                try {
                    console.log('🎬 Fetching detailed video production prompt from backend...');
                    
                    // Get scene context
                    const scene = projectData.scenes?.find(s => s.shots?.some(sh => sh.id === shot.id));
                    const sceneContext = scene?.description || scene?.title || '';
                    
                    // Get main character name
                    const mainCharacter = characters[0];
                    const characterName = mainCharacter?.name || '';
                    
                    const detailedPrompt = await generateVideoProductionPrompt({
                        shotDescription: shot.description || '',
                        characterName: characterName,
                        sceneContext: sceneContext,
                        durationSeconds: duration,
                        aspectRatio: projectData.aspectRatio || '16:9',
                        mood: shot.mood || scene?.mood || '',
                        genre: projectData.genre || ''
                    });
                    
                    if (detailedPrompt) {
                        console.log('✅ Detailed production prompt received from backend');
                        console.log('📋 Full detailedPrompt structure:', JSON.stringify(detailedPrompt, null, 2));
                        setProductionPrompt(detailedPrompt);
                        
                        // Extract the main video prompt text
                        const mainPromptText = detailedPrompt.video_prompt?.main_prompt || 
                                               detailedPrompt.video_prompt?.prompt ||
                                               detailedPrompt.main_prompt ||
                                               detailedPrompt.prompt || '';
                        
                        console.log('📝 Main prompt text:', mainPromptText?.substring(0, 200));
                        
                        // Use character_actions from backend if available
                        const rawActions = detailedPrompt.character_actions || 
                                       detailedPrompt.actions || 
                                       detailedPrompt.video_prompt?.actions ||
                                       [];
                        
                        console.log('🎬 Raw actions from backend:', JSON.stringify(rawActions, null, 2));
                        
                        // Flatten the nested character_actions structure
                        // Backend returns: [{character_name: "Anika", timeline: [{timecode, action, duration_ms}]}, ...]
                        // We need: [{startTime, endTime, action}, ...]
                        let flattenedActions = [];
                        
                        if (rawActions && rawActions.length > 0) {
                            // Check if it's the nested format (with character_name and timeline)
                            if (rawActions[0]?.timeline || rawActions[0]?.character_name) {
                                console.log('📋 Detected nested character_actions format, flattening...');
                                console.log('📋 Number of character blocks:', rawActions.length);
                                
                                rawActions.forEach((charAction, charIndex) => {
                                    const charName = charAction.character_name || 'Character';
                                    const timeline = charAction.timeline || [];
                                    
                                    console.log(`📋 Processing character ${charIndex + 1}: ${charName}, timeline entries: ${timeline.length}`);
                                    
                                    timeline.forEach((timelineItem, timelineIndex) => {
                                        // Parse timecode (format: "00:00" or "00:2.5" or "00:04")
                                        let startSeconds = 0;
                                        const timecode = timelineItem.timecode || '00:00';
                                        
                                        // Handle different timecode formats
                                        if (timecode.includes(':')) {
                                            const parts = timecode.split(':');
                                            const minutes = parseInt(parts[0]) || 0;
                                            const seconds = parseFloat(parts[1]) || 0;
                                            startSeconds = minutes * 60 + seconds;
                                        } else {
                                            startSeconds = parseFloat(timecode) || 0;
                                        }
                                        
                                        // Calculate end time from duration_ms or estimate
                                        const durationMs = timelineItem.duration_ms || 2000;
                                        const endSeconds = startSeconds + (durationMs / 1000);
                                        
                                        // Build action text with character name and emotion
                                        const actionText = timelineItem.action || timelineItem.description || '';
                                        const emotion = timelineItem.emotion ? ` [${timelineItem.emotion}]` : '';
                                        const fullActionText = `${charName}: ${actionText}${emotion}`;
                                        
                                        console.log(`📋 Action ${timelineIndex + 1} for ${charName}: "${actionText}" (${timecode} -> ${startSeconds.toFixed(1)}s-${endSeconds.toFixed(1)}s)`);
                                        
                                        flattenedActions.push({
                                            id: uuidv4(),
                                            startTime: `${startSeconds.toFixed(1)}s`,
                                            endTime: `${endSeconds.toFixed(1)}s`,
                                            action: fullActionText,
                                            character: charName,
                                            emotion: timelineItem.emotion,
                                            rawAction: actionText
                                        });
                                    });
                                });
                                
                                // Sort by start time
                                flattenedActions.sort((a, b) => {
                                    const aStart = parseFloat(a.startTime);
                                    const bStart = parseFloat(b.startTime);
                                    return aStart - bStart;
                                });
                                
                                console.log('📋 Final flattened actions count:', flattenedActions.length);
                                console.log('📋 Final actions:', flattenedActions.map(a => ({
                                    time: `${a.startTime}-${a.endTime}`,
                                    action: a.action
                                })));
                            } else {
                                // It's already a flat format
                                console.log('📋 Detected flat actions format');
                                flattenedActions = rawActions.map((action, idx) => {
                                    const actionText = action.action || action.description || action.text || action.prompt || '';
                                    const startTime = action.start_time || action.startTime || action.start || `${(idx * (duration / rawActions.length)).toFixed(1)}s`;
                                    const endTime = action.end_time || action.endTime || action.end || `${((idx + 1) * (duration / rawActions.length)).toFixed(1)}s`;
                                    
                                    return {
                                        id: uuidv4(),
                                        startTime: typeof startTime === 'number' ? `${startTime.toFixed(1)}s` : startTime,
                                        endTime: typeof endTime === 'number' ? `${endTime.toFixed(1)}s` : endTime,
                                        action: actionText
                                    };
                                });
                            }
                        }
                        
                        if (flattenedActions.length > 0) {
                            console.log(`✅ Setting ${flattenedActions.length} timed actions`);
                            setTimedActions(flattenedActions);
                            
                            // Update camera shot if provided
                            if (detailedPrompt.camera?.movement) {
                                const matchingCamera = CAMERA_SHOTS.find(c => 
                                    c.toLowerCase().includes(detailedPrompt.camera.movement.toLowerCase()) ||
                                    detailedPrompt.camera.movement.toLowerCase().includes(c.toLowerCase())
                                );
                                if (matchingCamera) {
                                    setCameraShot(matchingCamera);
                                }
                            }
                            
                            // Extract audio data (BGM, SFX, Ambient) from backend response
                            if (detailedPrompt.audio) {
                                console.log('🎵 Audio data from backend:', detailedPrompt.audio);
                                
                                // Extract BGM
                                if (detailedPrompt.audio.bgm) {
                                    const bgmData = detailedPrompt.audio.bgm;
                                    const bgmText = bgmData.description || '';
                                    const bgmMood = bgmData.mood ? ` (${bgmData.mood})` : '';
                                    const bgmTempo = bgmData.tempo_bpm ? ` - ${bgmData.tempo_bpm} BPM` : '';
                                    setBgm(`${bgmText}${bgmMood}${bgmTempo}`.trim());
                                    console.log('🎵 BGM set:', bgmText);
                                }
                                
                                // Extract SFX
                                if (detailedPrompt.audio.sfx && Array.isArray(detailedPrompt.audio.sfx)) {
                                    const sfxDescriptions = detailedPrompt.audio.sfx
                                        .map(s => {
                                            const sound = s.sound || s.description || '';
                                            const time = s.timecode ? ` @${s.timecode}` : '';
                                            return `${sound}${time}`;
                                        })
                                        .filter(s => s.trim())
                                        .join('; ');
                                    setSfx(sfxDescriptions);
                                    console.log('🔊 SFX set:', sfxDescriptions);
                                }
                                
                                // Extract Ambient sounds
                                if (detailedPrompt.audio.ambient) {
                                    const ambientData = detailedPrompt.audio.ambient;
                                    const ambientText = ambientData.description || '';
                                    setAmbient(ambientText);
                                    console.log('🌿 Ambient set:', ambientText);
                                }
                            }
                            
                            // Extract VFX if present
                            if (detailedPrompt.vfx || detailedPrompt.visual_effects) {
                                const vfxData = detailedPrompt.vfx || detailedPrompt.visual_effects;
                                if (Array.isArray(vfxData)) {
                                    const vfxDescriptions = vfxData
                                        .map(v => v.effect || v.description || v)
                                        .filter(v => v)
                                        .join('; ');
                                    setVfx(vfxDescriptions);
                                    console.log('✨ VFX set:', vfxDescriptions);
                                } else if (typeof vfxData === 'string') {
                                    setVfx(vfxData);
                                }
                            }
                            
                            return; // Successfully used backend prompt
                        } else if (mainPromptText) {
                            // Fallback: Use main_prompt as a single action if no character_actions
                            console.log('📋 No character_actions found, using main_prompt as single action');
                            setTimedActions([{
                                id: uuidv4(),
                                startTime: '0.0s',
                                endTime: `${duration.toFixed(1)}s`,
                                action: mainPromptText
                            }]);
                            
                            // Update camera shot if provided
                            if (detailedPrompt.camera?.movement) {
                                const matchingCamera = CAMERA_SHOTS.find(c => 
                                    c.toLowerCase().includes(detailedPrompt.camera.movement.toLowerCase()) ||
                                    detailedPrompt.camera.movement.toLowerCase().includes(c.toLowerCase())
                                );
                                if (matchingCamera) {
                                    setCameraShot(matchingCamera);
                                }
                            }
                            
                            // Extract audio data (BGM, SFX, Ambient) from backend response
                            if (detailedPrompt.audio) {
                                console.log('🎵 Audio data from backend:', detailedPrompt.audio);
                                
                                // Extract BGM
                                if (detailedPrompt.audio.bgm) {
                                    const bgmData = detailedPrompt.audio.bgm;
                                    const bgmText = bgmData.description || '';
                                    const bgmMood = bgmData.mood ? ` (${bgmData.mood})` : '';
                                    const bgmTempo = bgmData.tempo_bpm ? ` - ${bgmData.tempo_bpm} BPM` : '';
                                    setBgm(`${bgmText}${bgmMood}${bgmTempo}`.trim());
                                    console.log('🎵 BGM set:', bgmText);
                                }
                                
                                // Extract SFX
                                if (detailedPrompt.audio.sfx && Array.isArray(detailedPrompt.audio.sfx)) {
                                    const sfxDescriptions = detailedPrompt.audio.sfx
                                        .map(s => {
                                            const sound = s.sound || s.description || '';
                                            const time = s.timecode ? ` @${s.timecode}` : '';
                                            return `${sound}${time}`;
                                        })
                                        .filter(s => s.trim())
                                        .join('; ');
                                    setSfx(sfxDescriptions);
                                    console.log('🔊 SFX set:', sfxDescriptions);
                                }
                                
                                // Extract Ambient sounds
                                if (detailedPrompt.audio.ambient) {
                                    const ambientData = detailedPrompt.audio.ambient;
                                    const ambientText = ambientData.description || '';
                                    setAmbient(ambientText);
                                    console.log('🌿 Ambient set:', ambientText);
                                }
                            }
                            
                            // Extract VFX if present
                            if (detailedPrompt.vfx || detailedPrompt.visual_effects) {
                                const vfxData = detailedPrompt.vfx || detailedPrompt.visual_effects;
                                if (Array.isArray(vfxData)) {
                                    const vfxDescriptions = vfxData
                                        .map(v => v.effect || v.description || v)
                                        .filter(v => v)
                                        .join('; ');
                                    setVfx(vfxDescriptions);
                                    console.log('✨ VFX set:', vfxDescriptions);
                                } else if (typeof vfxData === 'string') {
                                    setVfx(vfxData);
                                }
                            }
                            
                            return; // Successfully used backend prompt
                        }
                    }
                } catch (backendErr) {
                    console.warn('⚠️ Backend production prompt failed, falling back to local generation:', backendErr.message);
                }
                
                // Fallback to local generation if backend fails
                console.log('🎬 Using local video prompt generation...');
                const generatedActions = await generateVideoPrompt(shot, characters, projectData.script, duration);
                setTimedActions(generatedActions.map(a => ({ ...a, id: uuidv4() })));
            } catch (err) {
                console.error(err);
                setError("Failed to generate a timed prompt. Please write one manually.");
                setTimedActions([{ id: uuidv4(), startTime: "0.0s", endTime: `${duration.toFixed(1)}s`, action: shot.description || '' }]);
            } finally {
                setIsGeneratingPrompt(false);
            }
        };

        fetchPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shot.id]); // Only re-fetch when shot changes, not when duration changes
    
    const handleAddTimedAction = () => {
        const lastTime = timedActions.length > 0 ? timedActions[timedActions.length - 1].endTime : "0.0s";
        const newAction = { id: uuidv4(), startTime: lastTime, endTime: lastTime, action: "" };
        setTimedActions(prev => [...prev, newAction]);
    };

    const handleRemoveTimedAction = (id) => {
        setTimedActions(prev => prev.filter(a => a.id !== id));
    };
    
    const handleUpdateTimedAction = (id, updates) => {
        setTimedActions(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
    };

    const handleDialogueChange = (characterId, update) => {
        setDialogues(prev => ({
            ...prev,
            [characterId]: { ...(prev[characterId] || { text: '', startTimecode: '0.0s', endTimecode: '3.0s' }), ...update }
        }));
    };
    
    const handleVoiceSelect = (voiceId, voiceName) => {
        if (!selectingVoiceForCharId || !onUpdateCharacter) return;
        
        const character = projectData.characters.find(c => c.id === selectingVoiceForCharId);
        if (character) {
            onUpdateCharacter({
                ...character,
                elevenLabsVoiceId: voiceId,
                audio: { ...character.audio, name: voiceName }
            });
        }
        setShowVoiceModal(false);
        setSelectingVoiceForCharId(null);
    };

    const handleTriggerGenerateSpeech = async (characterId, text) => {
        const character = projectData.characters.find(c => c.id === characterId);
        if (!character?.elevenLabsVoiceId || !text) return;

        setSpeechGenStatus(prev => ({ ...prev, [characterId]: { status: 'generating', error: null } }));

        try {
            const generatedAudioData = await generateSpeechWithElevenLabs(text, character.elevenLabsVoiceId);
            handleDialogueChange(characterId, {
                clonedAudio: {
                    name: `dialogue-tts-${characterId}-${Date.now()}.mp3`,
                    data: generatedAudioData,
                    mimeType: 'audio/mpeg',
                }
            });
            setSpeechGenStatus(prev => ({ ...prev, [characterId]: { status: 'idle', error: null } }));
        } catch(err) {
            const msg = err instanceof Error ? err.message : "Speech generation failed";
            setSpeechGenStatus(prev => ({ ...prev, [characterId]: { status: 'error', error: msg } }));
        }
    };
    
    const handleAddTextOverlay = () => {
        const newOverlay = {
            id: uuidv4(),
            content: 'Your text here',
            aiStyle: true,
            fontFamily: FONT_OPTIONS[0],
            fontSize: 48,
            color: '#FFFFFF'
        };
        setTextOverlays(prev => [...prev, newOverlay]);
    };

    const handleUpdateTextOverlay = (id, updates) => {
        setTextOverlays(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
    };

    const handleRemoveTextOverlay = (id) => {
        setTextOverlays(prev => prev.filter(o => o.id !== id));
    };

    const constructFinalPrompt = () => {
        const cinematicActions = timedActions
            .map(a => `@[${a.startTime}-${a.endTime}] ${a.action?.trim()}`)
            .join(' ');
            
        let finalPrompt = `[${cameraShot}] ${cinematicActions}`;
        
        const dialogueEntries = Object.entries(dialogues)
            .filter(([charId]) => projectData.characters.find(c => c.id === charId)?.category !== 'narrator')
            .filter(([, D]) => typeof D.text === 'string' && D.text?.trim() !== '')
            .map(([charId, D]) => {
                const charName = projectData.characters.find(c => c.id === charId)?.name || 'Character';
                return `@[${D.startTimecode}-${D.endTimecode}] ${charName}: "${D.text?.trim() || ''}"`;
            });

        if (dialogueEntries.length > 0) {
            finalPrompt += ` [Dialogue]: ${dialogueEntries.join(' ')}.`;
        }
        
        if (narrator && dialogues[narrator.id]?.text?.trim()) {
            finalPrompt += ` [Narration]: "${dialogues[narrator.id].text.trim()}".`;
        }

        if (sfx.trim()) {
            finalPrompt += ` [SFX]: ${sfx.trim()}.`;
        }
        if (vfx.trim()) {
            finalPrompt += ` [VFX]: ${vfx.trim()}.`;
        }
        if (bgm.trim()) {
            finalPrompt += ` [BGM]: ${bgm.trim()}.`;
        }
        if (ambient.trim()) {
            finalPrompt += ` [Ambient]: ${ambient.trim()}.`;
        }
        
        const textOverlayEntries = textOverlays.map(overlay => {
            if (!overlay.content?.trim()) return '';
            if (overlay.aiStyle) {
                return `Add text overlay: "${overlay.content}". Use a cinematic style that fits the scene.`;
            } else {
                return `Add text overlay: "${overlay.content}". Font: ${overlay.fontFamily}, Size: ${overlay.fontSize}px, Color: ${overlay.color}.`;
            }
        }).filter(Boolean);

        if (textOverlayEntries.length > 0) {
            finalPrompt += ` [Text Overlays]: ${textOverlayEntries.join(' ')}`;
        }
        
        return finalPrompt;
    };

    const attemptVideoGeneration = async (modelId, finalPrompt) => {
        const modelName = availableVideoModels.find(m => m.id === modelId)?.name || modelId;
        setLoadingMessage(`Generating with ${modelName}...`);
        
        // Prepare structured dialogues data for proper voice/character mapping
        
        const dialoguesData = Object.entries(dialogues)
            .filter(([charId, D]) => {
                // Find character in projectData.characters OR in the characters prop
                const charFromProject = projectData.characters.find(c => c.id === charId);
                const charFromProp = characters.find(c => c.id === charId);
                const char = charFromProject || charFromProp;
                
                const hasText = typeof D.text === 'string' && D.text?.trim() !== '';
                const isNotNarrator = char && char.category !== 'narrator';
                
                return char && isNotNarrator && hasText;
            })
            .map(([charId, D]) => {
                const charFromProject = projectData.characters.find(c => c.id === charId);
                const charFromProp = characters.find(c => c.id === charId);
                const char = charFromProject || charFromProp;
                return {
                    character_id: charId,
                    character_name: char?.name || 'Character',
                    start_time: D.startTimecode,
                    end_time: D.endTimecode,
                    text: D.text?.trim() || '',
                    order: D.order || 0,
                    // Include character image for visual reference
                    character_image: char?.image || null
                };
            })
            .sort((a, b) => {
                // Sort by start time to ensure correct order
                const aTime = parseFloat(a.start_time) || 0;
                const bTime = parseFloat(b.start_time) || 0;
                return aTime - bTime;
            });
        
        // Prepare cinematic actions with timecodes
        const cinematicActions = timedActions
            .filter(a => a.action?.trim())
            .map(a => ({
                start_time: a.startTime,
                end_time: a.endTime,
                action: a.action?.trim(),
                character: a.character || null,
                emotion: a.emotion || null
            }));
        
        console.log('🎭 Dialogues being sent to backend:', dialoguesData);
        console.log('🎬 Cinematic actions being sent:', cinematicActions);
        
        // Use the unified AI service for all models
        // Pass the model ID to the backend which will route to the correct provider
        const { generateVideo } = await import('../services/aiService');
        
        const result = await generateVideo(
            finalPrompt,
            duration,
            modelId,  // Pass the model ID/key to backend
            projectData.aspectRatio,
            shot.startFrameImage || null,
            shot.endFrameImage || null,
            setLoadingMessage,
            dialoguesData,      // Pass structured dialogues
            cinematicActions    // Pass cinematic actions
        );
        
        if (!result.videoUrl) {
            throw new Error('No video URL returned from backend');
        }
        
        return result; // Return full result including creditsUsed
    };

    const handleGenerateVideo = async () => {
        if (timedActions.every(a => !a.action || !a.action.trim())) {
            setError("Prompt cannot be empty.");
            return;
        }

        // Don't deduct upfront - backend handles credit deduction
        // if (onDeductCredits && !onDeductCredits(CREDIT_COSTS.VIDEO_GENERATION)) return;

        setGeneratedVideoUrl(null);
        setIsGeneratingVideo(true);
        setError(null);
        try {
            const finalPrompt = constructFinalPrompt();
            const result = await attemptVideoGeneration(videoModel, finalPrompt);
            setGeneratedVideoUrl(result.videoUrl);
            
            // Track credits used from backend response (with remaining credits and job ID)
            if (onTrackCreditsUsed && result.creditsUsed) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }
        } catch (err) {
            console.error(err);
            let errorMessage = err instanceof Error ? err.message : 'An unknown error occurred during video generation.';
            
            // Handle insufficient credits error
            if (errorMessage.includes('INSUFFICIENT_CREDITS') || errorMessage.includes('Not enough credits')) {
                errorMessage = 'Not enough credits. Please purchase more credits from your dashboard.';
            }
            
            setError(errorMessage);
        } finally {
            setIsGeneratingVideo(false);
            setLoadingMessage('');
        }
    };
    
    const handleSaveToProject = async () => {
        setIsSaving(true);
        setError(null);

        const dialogueAudios = Object.keys(dialogues)
            .filter(charId => projectData.characters.find(c => c.id === charId)?.category !== 'narrator')
            .map(charId => ({ charId, dialogue: dialogues[charId] }))
            .filter(({ dialogue }) => dialogue.clonedAudio)
            .map(({ charId, dialogue }) => ({
                characterId: charId,
                text: dialogue.text,
                audioData: dialogue.clonedAudio,
            }));
            
        let narrationAudioPayload;
        if (narrator && dialogues[narrator.id]?.clonedAudio) {
            narrationAudioPayload = {
                characterId: narrator.id,
                text: dialogues[narrator.id].text,
                audioData: dialogues[narrator.id].clonedAudio,
            };
        }

        // Get the actual video duration for use in the timeline
        let actualVideoDuration = duration; // Default to requested duration
        if (generatedVideoUrl) {
            try {
                actualVideoDuration = await getActualVideoDuration(generatedVideoUrl);
                console.log('📹 Actual video duration:', actualVideoDuration, 'seconds');
            } catch (e) {
                console.warn('⚠️ Could not get video duration, using requested duration:', duration);
            }
        }

        onSave(shot.id, {
            videoUrl: generatedVideoUrl,
            videoDuration: actualVideoDuration, // Store the actual video duration
            dialogueAudios: dialogueAudios.length > 0 ? dialogueAudios : undefined,
            narrationAudio: narrationAudioPayload,
            textOverlays: textOverlays.length > 0 ? textOverlays : undefined,
        });
        setIsSaving(false);
    };
    
    // Helper function to get actual video duration
    const getActualVideoDuration = (videoUrl) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.crossOrigin = 'anonymous';
            
            let resolved = false;
            
            const cleanup = () => {
                video.onloadedmetadata = null;
                video.ondurationchange = null;
                video.onerror = null;
            };
            
            video.onloadedmetadata = () => {
                if (!resolved && video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                    resolved = true;
                    cleanup();
                    resolve(video.duration);
                }
            };
            
            video.ondurationchange = () => {
                if (!resolved && video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                    resolved = true;
                    cleanup();
                    resolve(video.duration);
                }
            };
            
            video.onerror = () => {
                if (!resolved) {
                    resolved = true;
                    cleanup();
                    reject(new Error('Video load error'));
                }
            };
            
            setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    cleanup();
                    reject(new Error('Video duration timeout'));
                }
            }, 5000);
            
            video.src = videoUrl;
            video.load();
        });
    };
    
    const handleDownload = async () => {
        if (!generatedVideoUrl) return;
        setError(null);
        try {
            const response = await fetch(generatedVideoUrl);
            if (!response.ok) throw new Error('Failed to fetch video data for download.');
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = `loopai_shot_${shot.id}.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(objectUrl);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "An unknown error occurred during download.";
            console.error("Download failed:", e);
            setError(`Video download failed. ${errorMessage}`);
        }
    };
    
    const renderMainContent = () => {
        if (isGeneratingVideo || isSaving) {
            return (
                <div className="text-center">
                    <LoadingSpinner className="w-12 h-12 mx-auto mb-4 animate-spin" />
                    <h3 className="text-2xl font-semibold text-gray-300 mb-2">{isSaving ? 'Saving...' : 'Creating Magic...'}</h3>
                    <p className="text-lg text-purple-400">{loadingMessage}</p>
                </div>
            );
        }

        if (generatedVideoUrl) {
            return (
                 <video
                    key={generatedVideoUrl}
                    src={generatedVideoUrl}
                    controls
                    autoPlay
                    loop
                    className="w-full h-full object-contain"
                >
                    Your browser does not support the video tag.
                </video>
            )
        }

        return (
            <div className="w-full flex items-center justify-center gap-4">
                <div className="w-1/2 aspect-video bg-black rounded-lg flex flex-col items-center justify-center relative">
                    <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full z-10">Start Frame</span>
                    {shot.startFrameImage && <img src={shot.startFrameImage} alt="Start frame" className="w-full h-full object-contain rounded-lg" crossOrigin="anonymous" referrerPolicy="no-referrer" />}
                </div>
                <div className="w-1/2 aspect-video bg-black rounded-lg flex flex-col items-center justify-center relative">
                    <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full z-10">End Frame</span>
                    {shot.endFrameImage && <img src={shot.endFrameImage} alt="End frame" className="w-full h-full object-contain rounded-lg" crossOrigin="anonymous" referrerPolicy="no-referrer" />}
                </div>
            </div>
        )
    }
    
    const renderFooter = () => {
        const hasContentToSave = generatedVideoUrl || Object.values(dialogues).some(d => d.clonedAudio) || textOverlays.length > 0;
        const isActionEmpty = timedActions.every(a => !a.action || !a.action.trim());

        const isGenerateDisabled = isGeneratingPrompt || 
                               isGeneratingVideo || 
                               isSaving ||
                               isActionEmpty || 
                               !shot.startFrameImage;
                               
        if (generatedVideoUrl) {
            return (
                <>
                    <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                        Close
                    </button>
                    <div className="flex-grow flex justify-end items-center space-x-4">
                        <button 
                            onClick={handleDownload}
                            className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                        >
                            <DownloadIcon className="w-5 h-5 mr-2" />
                            Download
                        </button>
                        <button 
                            onClick={handleGenerateVideo}
                            disabled={isGenerateDisabled}
                            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                        >
                            <MagicWandIcon className="w-5 h-5 mr-2" />
                            Re-generate
                        </button>
                         <button 
                            onClick={handleSaveToProject}
                            disabled={isSaving}
                            className="bg-green-600 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                        >
                            <SaveIcon className="w-5 h-5 mr-2" />
                            Save to Project
                        </button>
                    </div>
                </>
            );
        }

        return (
            <>
                <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                    Cancel
                </button>
                {hasContentToSave && (
                     <button 
                        onClick={handleSaveToProject}
                        disabled={isSaving}
                        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                    >
                        {isSaving ? <LoadingSpinner /> : <SaveIcon className="w-5 h-5 mr-2" />}
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                )}
                <button 
                    onClick={handleGenerateVideo}
                    disabled={isGenerateDisabled}
                    className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                >
                    {isGeneratingVideo ? <LoadingSpinner /> : <MagicWandIcon className="w-5 h-5 mr-2" />}
                    {isGeneratingVideo ? 'Generating...' : 'Generate Video'}
                </button>
            </>
        )
    }

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose} role="dialog" aria-modal="true"
        >
            {showVoiceModal && (
                <ElevenLabsVoiceModal
                    onClose={() => setShowVoiceModal(false)}
                    onSelect={handleVoiceSelect}
                />
            )}
            <div
                className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <header className="p-4 flex justify-between items-center border-b border-gray-700 shrink-0">
                    <h2 className="text-xl font-bold">Shot Pre-Production</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-700"><CloseIcon className="w-6 h-6" /></button>
                </header>

                <div className="flex-grow flex flex-col md:flex-row overflow-auto">
                    <main className="flex-grow flex flex-col items-center justify-center p-4 bg-black/30">
                        {renderMainContent()}
                    </main>

                    <aside className="w-full md:w-[400px] bg-[#101018] flex flex-col overflow-y-auto shrink-0">
                        <div className="flex border-b border-gray-700 shrink-0">
                            <TabButton name="Prompt" tabId="prompt" activeTab={activeTab} setActiveTab={setActiveTab} />
                            {characters.length > 0 && <TabButton name="Dialogue" tabId="dialogue" activeTab={activeTab} setActiveTab={setActiveTab} />}
                            {narrator && <TabButton name="Narration" tabId="narration" activeTab={activeTab} setActiveTab={setActiveTab} />}
                            <TabButton name="Text" tabId="text" activeTab={activeTab} setActiveTab={setActiveTab} />
                        </div>
                        <div className="p-3 space-y-3 overflow-y-auto">
                            {error && <div className="bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg text-sm">{error}</div>}
                            
                            {activeTab === 'prompt' && (
                                <>
                                    <div className="border border-gray-700 rounded-lg p-3">
                                        <label className="text-sm font-semibold mb-2 block text-purple-300">Cinematic Prompt</label>
                                        {isGeneratingPrompt ? (
                                            <div className="w-full h-24 p-2 bg-gray-700/60 rounded-md flex items-center justify-center">
                                                <LoadingSpinner className="w-6 h-6 mr-2" />
                                                <span>Generating prompt...</span>
                                            </div>
                                        ) : (
                                            <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                                                {timedActions.map((action, index) => (
                                                    <div key={action.id} className="flex items-start gap-2">
                                                        <div className="flex items-center gap-1">
                                                            <input type="text" value={action.startTime} onChange={e => handleUpdateTimedAction(action.id, { startTime: e.target.value })} className="w-16 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm" placeholder="0.0s" />
                                                            <span className="text-gray-500">-</span>
                                                            <input type="text" value={action.endTime} onChange={e => handleUpdateTimedAction(action.id, { endTime: e.target.value })} className="w-16 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm" placeholder="4.0s" />
                                                        </div>
                                                        <textarea value={action.action} onChange={e => handleUpdateTimedAction(action.id, { action: e.target.value })} rows={2} className="flex-grow p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none" placeholder={`Action ${index + 1}...`} />
                                                        <button onClick={() => handleRemoveTimedAction(action.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-full flex-shrink-0 self-center"><TrashIcon className="w-4 h-4" /></button>
                                                    </div>
                                                ))}
                                                <button onClick={handleAddTimedAction} className="w-full text-xs bg-indigo-600/50 hover:bg-indigo-600 text-white font-semibold py-1 rounded-md flex items-center justify-center"><PlusIcon className="w-3 h-3 mr-1" /> Add Action</button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="border border-gray-700 rounded-lg p-3 space-y-3">
                                        <label className="text-sm font-semibold block text-purple-300">Video Settings</label>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">Model</label>
                                            {isLoadingModels ? (
                                                <div className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm flex items-center">
                                                    <LoadingSpinner className="w-4 h-4 mr-2" />
                                                    Loading models...
                                                </div>
                                            ) : (
                                                <select value={videoModel} onChange={e => setVideoModel(e.target.value)} className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" disabled={isGeneratingVideo}>
                                                    {availableVideoModels.map(model => (
                                                        <option key={model.id} value={model.id} disabled={model.disabled}>
                                                            {model.name}{model.provider ? ` (${model.provider})` : ''}{model.costEstimate ? ` - ${model.costEstimate}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        {(videoModel === 'wan-2.2' || videoModel === 'kling-2.6' || videoModel === 'kling-2.5-turbo' || videoModel === 'sora-2' || videoModel === 'ltx-fast' || videoModel === 'minimax') && (
                                            <div className="space-y-3 mt-3">
                                                <div>
                                                    <label className="text-xs font-medium text-gray-400">Duration: {duration}s</label>
                                                    {videoModel === 'wan-2.2' ? (
                                                        <div className="flex gap-2 mt-1">
                                                            {[5].map(d => (
                                                                <button key={d} type="button" onClick={() => setDuration(d)} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${duration === d ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>{d}s (Fixed)</button>
                                                            ))}
                                                        </div>
                                                    ) : videoModel === 'sora-2' ? (
                                                        <div className="flex gap-2 mt-1">
                                                            {[4, 8, 12].map(d => (
                                                                <button key={d} type="button" onClick={() => setDuration(d)} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${duration === d ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>{d}s</button>
                                                            ))}
                                                        </div>
                                                    ) : videoModel === 'ltx-fast' ? (
                                                        <div className="flex gap-2 mt-1">
                                                            {[5, 8, 10].map(d => (
                                                                <button key={d} type="button" onClick={() => setDuration(d)} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${duration === d ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>{d}s</button>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <input
                                                                type="range"
                                                                min="5" max="10" step="5"
                                                                value={duration}
                                                                onChange={e => setDuration(parseInt(e.target.value, 10))}
                                                                className="w-full mt-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                                                disabled={isGeneratingVideo}
                                                            />
                                                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                                                                <span>5s</span>
                                                                <span>10s</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                                {(videoModel === 'wan-2.2' || videoModel === 'sora-2' || videoModel === 'ltx-fast') && (
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-400">Resolution</label>
                                                        <div className="flex gap-2 mt-1 flex-wrap">
                                                            {videoModel === 'wan-2.2' ? (
                                                                <>
                                                                    <button type="button" onClick={() => setResolution('480p')} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${resolution === '480p' ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>480p</button>
                                                                    <button type="button" onClick={() => setResolution('580p')} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${resolution === '580p' ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>580p</button>
                                                                    <button type="button" onClick={() => setResolution('720p')} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${resolution === '720p' ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>720p</button>
                                                                </>
                                                            ) : videoModel === 'sora-2' ? (
                                                                <>
                                                                    <button type="button" onClick={() => setResolution('auto')} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${resolution === 'auto' ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>Auto</button>
                                                                    <button type="button" onClick={() => setResolution('720p')} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${resolution === '720p' ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>720p</button>
                                                                </>
                                                            ) : (
                                                                (['720p', '1080p']).map(res => (
                                                                    <button key={res} type="button" onClick={() => setResolution(res)} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${resolution === res ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>{res}</button>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">Camera Movement</label>
                                            <select value={cameraShot} onChange={e => setCameraShot(e.target.value)} className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" disabled={isGeneratingVideo}>
                                                {CAMERA_SHOTS.map(shot => <option key={shot} value={shot}>{shot}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">SFX (Sound Effects)</label>
                                            <textarea
                                                value={sfx}
                                                onChange={e => setSfx(e.target.value)}
                                                placeholder="e.g., thunder, footsteps, door creaking..."
                                                rows={2}
                                                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">VFX (Visual Effects)</label>
                                            <textarea
                                                value={vfx}
                                                onChange={e => setVfx(e.target.value)}
                                                placeholder="e.g., explosions, sparkles, lens flare..."
                                                rows={2}
                                                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">🎵 BGM (Background Music)</label>
                                            <textarea
                                                value={bgm}
                                                onChange={e => setBgm(e.target.value)}
                                                placeholder="e.g., Gentle acoustic guitar, uplifting melody..."
                                                rows={2}
                                                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">🌿 Ambient Sound</label>
                                            <textarea
                                                value={ambient}
                                                onChange={e => setAmbient(e.target.value)}
                                                placeholder="e.g., City traffic, forest birds, ocean waves..."
                                                rows={2}
                                                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                             {activeTab === 'dialogue' && characters.length > 0 && (
                                <div className="border border-gray-700 rounded-lg p-3 space-y-4">
                                    <label className="text-sm font-semibold block text-purple-300">Dialogue & Voice Cloning</label>
                                    {/* Sort characters by their dialogue order (from script) */}
                                    {[...characters].sort((a, b) => {
                                        const aOrder = dialogues[a.id]?.order ?? 999;
                                        const bOrder = dialogues[b.id]?.order ?? 999;
                                        // If order is same, sort by start timecode
                                        if (aOrder === bOrder) {
                                            const aStart = parseFloat(dialogues[a.id]?.startTimecode || '999');
                                            const bStart = parseFloat(dialogues[b.id]?.startTimecode || '999');
                                            return aStart - bStart;
                                        }
                                        return aOrder - bOrder;
                                    }).map(char => (
                                        <DialogueAudioSection 
                                            key={char.id} 
                                            character={projectData.characters.find(c => c.id === char.id) || char} 
                                            dialogue={dialogues[char.id]} 
                                            onDialogueChange={(update) => handleDialogueChange(char.id, update)}
                                            onGenerateSpeech={() => handleTriggerGenerateSpeech(char.id, dialogues[char.id].text)}
                                            generationStatus={speechGenStatus[char.id]?.status || 'idle'}
                                            generationError={speechGenStatus[char.id]?.error || null}
                                            onSelectVoice={() => {
                                                setSelectingVoiceForCharId(char.id);
                                                setShowVoiceModal(true);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                            {activeTab === 'narration' && narrator && (
                                <div className="border border-gray-700 rounded-lg p-3 space-y-3">
                                    <label className="text-sm font-semibold block text-purple-300">Narration</label>
                                    <DialogueAudioSection 
                                        character={narrator} 
                                        dialogue={dialogues[narrator.id] || { text: '', startTimecode: '0.0s', endTimecode: '3.0s' }} 
                                        onDialogueChange={(update) => handleDialogueChange(narrator.id, update)}
                                        onGenerateSpeech={() => handleTriggerGenerateSpeech(narrator.id, dialogues[narrator.id]?.text)}
                                        generationStatus={speechGenStatus[narrator.id]?.status || 'idle'}
                                        generationError={speechGenStatus[narrator.id]?.error || null}
                                        onSelectVoice={() => {
                                            setSelectingVoiceForCharId(narrator.id);
                                            setShowVoiceModal(true);
                                        }}
                                    />
                                </div>
                            )}
                            {activeTab === 'text' && (
                                <div className="border border-gray-700 rounded-lg p-3 space-y-3">
                                    <div className="flex justify-between items-center">
                                        <label className="text-sm font-semibold text-purple-300">Text Overlays</label>
                                        <button onClick={handleAddTextOverlay} className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-1 px-2 rounded-md flex items-center">
                                            <PlusIcon className="w-3 h-3 mr-1" /> Add Text
                                        </button>
                                    </div>
                                    {textOverlays.map(overlay => (
                                        <div key={overlay.id} className="bg-gray-800/50 p-3 rounded-md space-y-2">
                                            <div className="flex justify-between items-center">
                                                <input
                                                    type="text"
                                                    value={overlay.content}
                                                    onChange={e => handleUpdateTextOverlay(overlay.id, { content: e.target.value })}
                                                    className="flex-grow p-1 bg-gray-700 border border-gray-600 rounded text-sm"
                                                    placeholder="Text content..."
                                                />
                                                <button onClick={() => handleRemoveTextOverlay(overlay.id)} className="ml-2 p-1 text-red-400 hover:bg-red-500/20 rounded">
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Switch checked={overlay.aiStyle} onChange={(val) => handleUpdateTextOverlay(overlay.id, { aiStyle: val })} />
                                                <span className="text-xs text-gray-400">AI Styling</span>
                                            </div>
                                            {!overlay.aiStyle && (
                                                <div className="grid grid-cols-2 gap-2">
                                                    <select value={overlay.fontFamily} onChange={e => handleUpdateTextOverlay(overlay.id, { fontFamily: e.target.value })} className="p-1 bg-gray-700 border border-gray-600 rounded text-xs">
                                                        {FONT_OPTIONS.map(font => <option key={font} value={font}>{font}</option>)}
                                                    </select>
                                                    <input type="number" value={overlay.fontSize} onChange={e => handleUpdateTextOverlay(overlay.id, { fontSize: parseInt(e.target.value) })} className="p-1 bg-gray-700 border border-gray-600 rounded text-xs" />
                                                    <input type="color" value={overlay.color} onChange={e => handleUpdateTextOverlay(overlay.id, { color: e.target.value })} className="col-span-2 h-8 bg-gray-700 border border-gray-600 rounded" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </aside>
                </div>

                <footer className="flex justify-between items-center p-4 border-t border-gray-700 shrink-0">
                    {renderFooter()}
                </footer>
            </div>
        </div>
    );
};
